/**
 * Phase 8 · Cluster C-48 — Route-scoped quick action chip row.
 *
 * Spec: docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.B
 *
 * Renders the chips for the current route from `ACTIONS_FOR_ROUTE`. Clicking
 * a chip fills the caller's composer with the action's `promptTemplate`
 * substituted with the current entity fields. The composer owns the send
 * flow — this component never dispatches by itself.
 *
 * Also shows the small "📍 {crumbs}" context line above the chip row with
 * the resolved scope (muted). When no chips resolve for the current route
 * we render just the context line.
 */

import { MapPin, Sparkles } from 'lucide-react';
import type { AgentQuickAction, AgentRouteContext } from '../context/types';
import { renderPromptTemplate } from '../context/actionsForRoute';

interface Props {
  ctx: AgentRouteContext;
  /**
   * Invoked with the rendered prompt text (placeholders substituted).
   * The caller is expected to place the text in the composer, not send
   * it — this matches the "fills composer" contract in §34.2.B.
   */
  onPickAction: (prompt: string, action: AgentQuickAction) => void;
  className?: string;
}

export function AgentQuickActions({ ctx, onPickAction, className }: Props) {
  const { availableActions, entity, route, scopeKind } = ctx;

  const crumb = buildCrumb({ route, scopeKind, entity });

  const handleClick = (action: AgentQuickAction) => {
    const prompt = renderPromptTemplate(action.promptTemplate, { route, entity: entity ?? null });
    onPickAction(prompt, action);
  };

  if (availableActions.length === 0) {
    return (
      <div
        className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] ${className ?? ''}`}
        style={{ color: 'var(--vscode-text-tertiary)' }}
        role="note"
      >
        <MapPin className="h-3 w-3" aria-hidden />
        <span>{crumb}</span>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-1.5 border-b px-3 py-2 ${className ?? ''}`}
      style={{ borderColor: 'var(--vscode-border-primary)' }}
    >
      <div
        className="flex items-center gap-1.5 text-[11px]"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        <MapPin className="h-3 w-3" aria-hidden />
        <span>{crumb}</span>
      </div>
      <div className="flex flex-wrap gap-1.5" role="list" aria-label="Quick actions">
        {availableActions.map((a) => (
          <button
            key={a.id}
            type="button"
            role="listitem"
            onClick={() => handleClick(a)}
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
            style={{
              borderColor: 'color-mix(in srgb, var(--ggj-accent,#10B981) 30%, var(--vscode-border-primary))',
              color: 'var(--vscode-text-primary)',
              background: 'var(--vscode-bg-elevated)',
            }}
            title={a.promptTemplate}
          >
            <Sparkles className="h-3 w-3" style={{ color: 'var(--ggj-accent,#10B981)' }} aria-hidden />
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function buildCrumb(ctx: Pick<AgentRouteContext, 'route' | 'scopeKind' | 'entity'>): string {
  const { route, scopeKind, entity } = ctx;
  const routeLabel = routeToLabel(route);
  if (entity) return `${routeLabel} › ${entity.id} — ${entity.label}`;
  if (scopeKind === 'canvas') return `${routeLabel} (canvas)`;
  return routeLabel;
}

function routeToLabel(route: string): string {
  if (route === '/' || route === '') return 'Home';
  if (route.startsWith('/requisitions')) return 'Requisitions';
  if (route.startsWith('/candidates')) return 'Candidates';
  if (route.startsWith('/applications')) return 'Applications';
  if (route.startsWith('/interviews')) return 'Interviews';
  if (route.startsWith('/clients')) return 'Clients';
  if (route.startsWith('/smart-match')) return 'Smart Match';
  const seg = route.split('/').filter(Boolean)[0] ?? 'App';
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}
